import { useCallback, useRef } from 'react';

interface CommittedRows {
    rowIds: readonly string[];
    retainedIds: ReadonlySet<string>;
}

const haveSameRowIds = (previous: readonly string[] | undefined, current: readonly string[]) =>
    previous !== undefined &&
    previous.length === current.length &&
    previous.every((id, index) => id === current[index]);

/**
 * Хранит только строки завершённых React-коммитов, не запоминая прерванные рендеры.
 * commitRows вызывается из layout-effect после монтажа фактического диапазона.
 */
export function useKeepMountedRowRange(rowIds: readonly string[]) {
    const committedRowsRef = useRef<CommittedRows>();

    const getRetainedIndexes = useCallback(() => {
        const committedRows = committedRowsRef.current;
        if (!haveSameRowIds(committedRows?.rowIds, rowIds)) {
            return [];
        }

        const indexes: number[] = [];
        rowIds.forEach((id, index) => {
            if (committedRows?.retainedIds.has(id)) {
                indexes.push(index);
            }
        });
        return indexes;
    }, [rowIds]);

    const commitRows = useCallback(
        (indexes: readonly number[]) => {
            const previous = committedRowsRef.current;
            const isSameRows = haveSameRowIds(previous?.rowIds, rowIds);
            if (
                isSameRows &&
                indexes.every(index => {
                    const id = rowIds[index];
                    return id === undefined || previous?.retainedIds.has(id);
                })
            ) {
                return;
            }

            const retainedIds = new Set(isSameRows ? previous?.retainedIds : undefined);
            indexes.forEach(index => {
                const id = rowIds[index];
                if (id !== undefined) {
                    retainedIds.add(id);
                }
            });

            // Эти строки уже смонтированы: новый render не нужен. Следующий диапазон прочитает накопленный набор.
            committedRowsRef.current = retainedIds.size > 0 ? { rowIds: [...rowIds], retainedIds } : undefined;
        },
        [rowIds]
    );

    return { getRetainedIndexes, commitRows };
}
